import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { enviroment } from 'src/environments/environment';
import { AiUsageResponse, AiUsageHistoryResponse } from '../model/response/ai-response';
import {
  AiSessionsResponse,
  AiSessionDetail,
  AiSessionDetailSession,
  AiFeedbackRequest,
  AiChatRequest,
  AiProfilesResponse,
  AiSseEvent,
  AiTraceItem,
} from '../model/response/ai-chat-response';
import { AiErrorResponse } from '../model/ai-error';
import { AiSkill, AiSkillUpsert } from '../model/response/ai-skill.model';

@Injectable({ providedIn: 'root' })
export class AiService {
  private baseUrl: string;
  private sseUrl: string;

  constructor(private http: HttpClient) {
    this.baseUrl = enviroment.BASE_URL_API;
    this.sseUrl = enviroment.BASE_URL_SSE;
  }

  // ─── Usage (existing) ───────────────────────────────────────────────

  getUsage(): Observable<AiUsageResponse> {
    return this.http.get<AiUsageResponse>(`${this.baseUrl}/AI/usage`);
  }

  getUsageHistory(page: number = 1, pageSize: number = 20): Observable<AiUsageHistoryResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<AiUsageHistoryResponse>(`${this.baseUrl}/AI/usage/history`, { params });
  }

  // ─── Sessions ───────────────────────────────────────────────────────

  getSessions(limit: number = 20, cursor?: string | null): Observable<AiSessionsResponse> {
    let params = new HttpParams().set('limit', limit.toString());
    if (cursor) params = params.set('cursor', cursor);
    return this.http.get<AiSessionsResponse>(`${this.baseUrl}/AI/sessions`, { params });
  }

  getSession(sessionId: string, limit: number = 100, cursor?: string | null): Observable<AiSessionDetail> {
    let params = new HttpParams().set('limit', limit.toString());
    if (cursor) params = params.set('cursor', cursor);
    return this.http.get<AiSessionDetail>(`${this.baseUrl}/AI/sessions/${sessionId}`, { params });
  }

  renameSession(sessionId: string, title: string): Observable<{ session: AiSessionDetailSession }> {
    return this.http.patch<{ session: AiSessionDetailSession }>(
      `${this.baseUrl}/AI/sessions/${sessionId}`,
      { title }
    );
  }

  deleteSession(sessionId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/AI/sessions/${sessionId}`);
  }

  // ─── Profile selector ──────────────────────────────────────────────

  //vitrine do seletor de perfis (Eco/Sport/Race); endpoint mora na ftcloud-api (baseUrl),
  //diferente do /AI/chat que fica na realtime (sseUrl) — não copiar a base do vizinho
  getProfiles(): Observable<AiProfilesResponse> {
    return this.http.get<AiProfilesResponse>(`${this.baseUrl}/AI/profiles`);
  }

  // ─── Chat (SSE via fetch) ──────────────────────────────────────────

  getAuthToken(): string {
    return sessionStorage.getItem('idToken') || '';
  }

  async sendMessageSSE(
    body: AiChatRequest,
    sessionId?: string | null,
    idempotencyKey?: string | null,
    onEvent?: (event: AiSseEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.getAuthToken()}`,
    };
    if (sessionId) headers['X-Session-Id'] = sessionId;
    if (idempotencyKey) headers['X-Idempotency-Key'] = idempotencyKey;

    const response = await fetch(`${this.sseUrl}/AI/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      let parsed: AiErrorResponse = { error: '' };
      try {
        parsed = await response.json();
      } catch {
        parsed = { error: await response.text().catch(() => '') };
      }
      throw { status: response.status, ...parsed };
    }

    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
      const json = await response.json();
      if (json.duplicate) {
        onEvent?.({ event: 'duplicate', data: json });
        return;
      }
    }

    const reader = response.body?.getReader();
    if (!reader) return;

    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          const dataStr = line.slice(6);
          try {
            const data = JSON.parse(dataStr);
            onEvent?.({ event: currentEvent, data });
          } catch {
            onEvent?.({ event: currentEvent, data: dataStr });
          }
        } else if (line.trim() === '') {
          currentEvent = '';
        }
      }
    }
  }

  // ─── Feedback ──────────────────────────────────────────────────────

  sendFeedback(body: AiFeedbackRequest): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/AI/chat/feedback`, body);
  }

  // ─── Trace (ADM) ───────────────────────────────────────────────────

  //só existe na realtime (sseUrl), não na API principal
  getTraceItem(sessionId: string, ref: string): Observable<AiTraceItem> {
    const params = new HttpParams().set('ref', ref);
    return this.http.get<AiTraceItem>(`${this.sseUrl}/AI/sessions/${sessionId}/trace-items`, { params });
  }

  // ─── Skills (user) ─────────────────────────────────────────────────

  getUserSkills(userId: string): Observable<AiSkill[]> {
    return this.http.get<AiSkill[]>(`${this.baseUrl}/user/${userId}/ai/skills`);
  }

  getCustomSkills(userId: string): Observable<AiSkill[]> {
    return this.http.get<AiSkill[]>(`${this.baseUrl}/user/${userId}/ai/skills/custom`);
  }

  createCustomSkill(userId: string, body: AiSkillUpsert): Observable<AiSkill> {
    return this.http.post<AiSkill>(`${this.baseUrl}/user/${userId}/ai/skills/custom`, body);
  }

  updateCustomSkill(userId: string, skillId: string, body: AiSkillUpsert): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/user/${userId}/ai/skills/custom/${skillId}`, body);
  }

  deleteCustomSkill(userId: string, skillId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/user/${userId}/ai/skills/custom/${skillId}`);
  }

  // ─── Skills (admin) ────────────────────────────────────────────────

  getAdminSkills(): Observable<AiSkill[]> {
    return this.http.get<AiSkill[]>(`${this.baseUrl}/admin/ai/skills`);
  }

  createAdminSkill(body: AiSkillUpsert): Observable<AiSkill> {
    return this.http.post<AiSkill>(`${this.baseUrl}/admin/ai/skills`, body);
  }

  updateAdminSkill(skillId: string, body: AiSkillUpsert): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/admin/ai/skills/${skillId}`, body);
  }

  deleteAdminSkill(skillId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/admin/ai/skills/${skillId}`);
  }
}
